import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid,  
	RCCHookToast,  
	RCCIcon,  
	RCCInput,  
	RCCServicesApi,  
	RCCTooltip
} from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import PageTitle from "src/components/PageTitle";
import { useParams } from "react-router-dom";
import Pagination from "src/components/Pagination";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import UserColumn from "src/components/DefaultColumnsList/User";
import FiltersContainer from "src/components/FiltersContainer";
import { useForm } from "react-hook-form";
import LovTrashTables from "src/components/Lov/TrashTables";
import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { differenceInDays } from "date-fns";
import Title from "src/components/Title";
import AppIcons from "src/components/AppIcons";

interface IFilters{
    filter: string;
    table: string;
}

const differenceBetweenDatesAsDaysInString = (date: Date, translate: (text: string, objKeys?: object | undefined) => string) => {
	const numberDays = differenceInDays(new Date(), new Date(date));
	if(numberDays === 0)
		return translate("Hoje");
	return `${numberDays} ${translate("dias")}`;
};

const Trash: React.FC = () => { 
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();
	const params = useParams<{
        table: string;
    }>();

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFilters>({ filter: "", table: params.table ?? "oftba001" });
	const {handleSubmit : handleSubmitFilters, control: controlFilters, register: registerFilters, reset: resetFilters } = useForm();
	
	 const submitFilters = (data: any) => {
	 	setFilters({ filter: data.filter, table: data.table });
	 	paginationRef.current?.reload();
	};

	const restore = async (id: number) => {
		confirm(
			translate("Restaurar"), 
			`${translate("Deseja realmente restaurar o item")}?`,
			async () => {
				await RCCServicesApi.postRequest({
					method: "Trash/Restore",
					urlParameters: {
						id,
						table: filters.table
					}
				});
				addToast({
					color: "green",
					description: translate("Restaurado com sucesso"),
					title: translate("Sucesso")
				});
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao restaurar"), addToast);
			},
			true
		);		
	};

	const deleteFunction = async (id: number) => {
		confirm(
			translate("Deletar"), 
			`${translate("Deseja realmente deletar o item permanente")}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Trash/Delete",
					urlParameters: {
						id,
						table: filters.table
					}
				});
				addToast({
					color: "green",
					description: translate("Deletado com sucesso"),
					title: translate("Sucesso")
				});
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao deletar"), addToast);
			},
			true
		);		
	};
	
	// List & Pagination
	 const paginationRef = useRef<IForwardPagination>(null);
	const colOptions = (row: any) => (
		<>
			<RCCTooltip
				color="grey"
				text={translate("Restaurar")}
				position="left"
				style={{ cursor: "pointer", marginRight: "1rem" }}
			>
				<RCCIcon name="upload" size={20} color="var(--grey)" onClick={() => restore(row.id)}/>
			</RCCTooltip>
			<RCCTooltip
				color="grey"
				text={translate("Deletar permanente")}
				position="left"
				style={{ cursor: "pointer" }}
			>
				<RCCIcon name="trash" size={20} color="var(--grey)" onClick={() => deleteFunction(row.id)}/>
			</RCCTooltip>
		</>	
	);
	const colDate = (row: any) => (
		<>
			{formatDate(row.date, currentLanguage, true)} <Title size="xs" color="grey">({differenceBetweenDatesAsDaysInString(row.date, translate)})</Title>
		</>
	);
	const colCreatedBy = (row: any) => (
		<UserColumn 
			name={row.createdByName}
			image={row.createdByImage}
			hover={false}
		/>
	);
	const colDeletedBy = (row: any) => (
		<UserColumn 
			name={row.modifiedByName}
			image={row.modifiedByImage}
			hover={false}
		/>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Lixeira")}
					icon={() => <AppIcons name="trash" />}	
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={
							showFilters 
							|| filters.filter 
								? "blue" : "grey"
						}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={
								showFilters  
								|| filters.filter   
									? "blue" : "grey"
							}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>		

				{/* Filters */}
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ filter: "", table: "oftba001" })}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						spacing={1}
						styleInline="@media (max-width: 960px){margin-bottom: 2.4rem; !important}"
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovTrashTables 
								control={controlFilters}
								name="table" 
								defaultValue={filters.table}							
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item	
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.filter}
							/>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
						

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="Trash/List"
					filtersData={filters}   
					columns={[
	  					{
							idColumn: "name",
							nameColumn: translate("Descrição"),
							xs: 12,
							sm: 4,
							type: "string",
							truncate: true
	  					},
						{
							idColumn: "userCreate",
							nameColumn: translate("Criado por"),
							xs: 12,
							sm: 2,
							cellRenderer: colCreatedBy,
							disableSort: true
						},
						{
							idColumn: "userDelete",
							nameColumn: translate("Excluído por"),
							xs: 12,
							sm: 2,
							cellRenderer: colDeletedBy,
							disableSort: true
						},
						{
							idColumn: "date",
							nameColumn: translate("Data de exclusão"),
							xs: 12,
							sm: 3,
							cellRenderer: colDate                           
	  					},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							xs: 12,
							sm: 1,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
				/>				
			</RCCGrid>
		</>
	);
};

export default Trash;
